import type { CrewLoadRow } from '../api';

interface Props {
  crew: CrewLoadRow[];
  loading?: boolean;
  language?: 'sv' | 'en';
}

export function CrewPanel({ crew, loading, language = 'sv' }: Props) {
  // Server delivers per-cleaner load already aggregated (next 30 days),
  // sorted highest-first. Each row carries name, jobs, points, kr_per_point.
  const totalAssigned = crew.reduce((s, m) => s + m.jobs, 0);
  const members = [...crew].sort((a, b) => b.jobs - a.jobs);

  const topShare =
    members.length > 0 && totalAssigned > 0
      ? Math.round((members[0].jobs / totalAssigned) * 100)
      : 0;

  const concentrationWarn = topShare > 60;

  return (
    <div className="cmd-panel cmd-panel-inner rounded p-4 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 shrink-0">
        <span className="cmd-label">BEMANNING — NÄSTA 30 DAGAR</span>
      </div>

      {/* Concentration stat */}
      {!loading && members.length > 0 && (
        <div
          className="flex items-center gap-2 mb-3 px-2 py-1.5 rounded shrink-0"
          style={{
            background: concentrationWarn
              ? 'rgba(185,126,31,0.08)'
              : 'rgba(27,127,77,0.06)',
            border: concentrationWarn
              ? '1px solid rgba(185,126,31,0.25)'
              : '1px solid rgba(27,127,77,0.2)',
          }}
        >
          <div
            className="w-1.5 h-1.5 rounded-full shrink-0"
            style={{ background: concentrationWarn ? '#B97E1F' : '#1B7F4D' }}
          />
          <span className="cmd-label">KONCENTRATION</span>
          <span
            className="ml-auto text-xs font-mono tabular-nums font-semibold"
            style={{ color: concentrationWarn ? '#B97E1F' : '#1B7F4D' }}
          >
            {topShare}%
          </span>
          {concentrationWarn && (
            <span
              className="cmd-label px-1 py-0.5 rounded text-[8px]"
              style={{ background: 'rgba(185,126,31,0.12)', color: '#B97E1F' }}
            >
              WARN
            </span>
          )}
        </div>
      )}

      {/* Bars */}
      <div className="flex-1 overflow-y-auto cmd-scroll space-y-2 min-h-0">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-8 rounded animate-pulse"
              style={{ background: 'rgba(7,51,49,0.05)' }}
            />
          ))
        ) : members.length === 0 ? (
          <p className="text-xs font-mono text-[#607C6E] py-2">
            {language === 'sv' ? 'Inga tilldelade jobb.' : 'No assigned jobs.'}
          </p>
        ) : (
          members.map((member) => {
            const share = totalAssigned > 0 ? member.jobs / totalAssigned : 0;

            return (
              <div key={member.user_id} className="space-y-1">
                {/* Name row */}
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-mono text-[#2E4640] truncate">
                    {member.name ?? member.user_id.slice(0, 8)}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    {member.kr_per_point != null && (
                      <span className="cmd-label text-[9px]">
                        {member.kr_per_point} kr/p
                      </span>
                    )}
                    <span className="text-xs font-mono tabular-nums text-[#8A6A47]">
                      {member.jobs}
                    </span>
                  </div>
                </div>

                {/* Bar */}
                <div
                  className="w-full h-1 rounded-full overflow-hidden"
                  style={{ background: 'rgba(7,51,49,0.08)' }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${(share * 100).toFixed(1)}%`,
                      background:
                        share > 0.6
                          ? 'rgba(185,126,31,0.6)'
                          : 'rgba(160,123,82,0.55)',
                    }}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
